// ============================================
// SUBIRIMAGEN.JS — Componente reutilizable de upload a Cloudinary
// ============================================
// Consume POST /api/upload/image (multipart con campo "imagen", opcional "folder").
// Backend en src/routes/upload.js — sin auth. Devuelve { ok, url, public_id, ... }.
//
// Props:
//   value: URL actual (modo edit) | null
//   onChange(url, data): se dispara despues del upload exitoso
//   onDelete: callback opcional; si esta presente aparece boton "Eliminar" e
//     intenta borrar el asset de Cloudinary tambien
//   folder: carpeta destino en Cloudinary (default 'beep-os/uploads')
//   label: texto del label superior (default 'Subir imagen', vacio = sin label)
//   aspectRatio: 'square' | '16:9' | '4:3' | 'free' (default 'free')
//   maxSizeMB: limite cliente (default 5, debe coincidir con el del backend)
//   acceptedTypes: array de extensiones (default jpg/jpeg/png/webp/gif)
//   disabled: bool

import React, { useState, useRef } from 'react';
import { API_BASE_CLI, PP } from './helpersClientes';
import { authHeaders } from '../utils/storage';

// Paleta Beep (alineada con BeepTV.js — para coherencia visual al integrarse)
const C = {
  blue700: '#265F97',
  blue500: '#5E99D2',
  blue300: '#9EC2E5',
  blue100: '#DEEBF7',
  gray700: '#3D4248',
  gray500: '#5E666E',
  gray100: '#D7D9DB',
  gray50: '#F3F4F4',
  rojo: '#DC2626',
  verde: '#16A34A',
  blanco: '#FFFFFF',
};

export default function SubirImagen({
  value = null,
  onChange,
  onDelete,
  folder = 'beep-os/uploads',
  label = 'Subir imagen',
  aspectRatio = 'free',
  maxSizeMB = 5,
  acceptedTypes = ['jpg', 'jpeg', 'png', 'webp', 'gif'],
  disabled = false,
  // ALMACEN v3 (2026-10-21): 'environment' abre DIRECTO la camara trasera del celular (foto del producto, del ticket,
  // de la etiqueta S/N, del equipo instalado). Sin capture el navegador pregunta galeria o camara.
  capture = null,
}) {
  const [estado, setEstado] = useState('idle'); // 'idle' | 'subiendo' | 'exito' | 'error'
  const [progreso, setProgreso] = useState(0);
  const [error, setError] = useState(null);
  const [previewLocal, setPreviewLocal] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const acceptStr = acceptedTypes.map(t => `image/${t}`).join(',');

  const aspectStyle = (() => {
    switch (aspectRatio) {
      case 'square': return { aspectRatio: '1 / 1' };
      case '16:9':   return { aspectRatio: '16 / 9' };
      case '4:3':    return { aspectRatio: '4 / 3' };
      default:       return { minHeight: 160 };
    }
  })();

  const validarArchivo = (file) => {
    const mime = (file.type || '').toLowerCase();
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const tipoOk = acceptedTypes.some(t => mime === `image/${t}` || ext === t);
    if (!tipoOk) {
      return `Tipo no permitido. Solo: ${acceptedTypes.join(', ')}`;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `La imagen supera los ${maxSizeMB} MB. Tiene ${(file.size / 1024 / 1024).toFixed(1)} MB.`;
    }
    return null;
  };

  const subir = (file) => {
    const err = validarArchivo(file);
    if (err) {
      setError(err);
      setEstado('error');
      return;
    }
    setError(null);

    // Preview local antes/durante de subir
    const reader = new FileReader();
    reader.onload = e => setPreviewLocal(e.target.result);
    reader.readAsDataURL(file);
    setEstado('subiendo');
    setProgreso(0);

    const formData = new FormData();
    formData.append('imagen', file);
    formData.append('folder', folder);

    // XMLHttpRequest para barra de progreso real (fetch no soporta progreso de upload nativamente)
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_CLI}/api/upload/image`);
    // ALMACEN v3: el endpoint ya exige sesion.
    const cab = authHeaders(); if (cab && cab.Authorization) xhr.setRequestHeader('Authorization', cab.Authorization);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setProgreso(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.ok && data.url) {
            setEstado('exito');
            setProgreso(100);
            setPreviewLocal(null);
            if (onChange) onChange(data.url, data);
          } else {
            setError(data.error || 'Respuesta inesperada del servidor');
            setEstado('error');
          }
        } catch (e) {
          setError('Error al procesar respuesta: ' + e.message);
          setEstado('error');
        }
      } else {
        let mensajeErr = 'Error del servidor: ' + xhr.status;
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.error) mensajeErr = data.error;
        } catch {}
        setError(mensajeErr);
        setEstado('error');
      }
    };

    xhr.onerror = () => {
      setError('Error de conexion. Verifica tu internet.');
      setEstado('error');
    };

    xhr.send(formData);
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (file) subir(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (file) subir(file);
  };

  const eliminar = () => {
    if (onDelete && value) {
      // Intentar borrar de Cloudinary tambien (extraer public_id de la URL).
      // URL tipica: https://res.cloudinary.com/.../upload/v123/folder/file.ext
      try {
        const match = value.match(/\/upload\/(?:v\d+\/)?(.+?)\.[a-z]+$/i);
        if (match && match[1]) {
          const publicId = match[1];
          fetch(`${API_BASE_CLI}/api/upload/image/${publicId}`, { method: 'DELETE' }).catch(() => {});
        }
      } catch {}
      onDelete();
    }
    setEstado('idle');
    setPreviewLocal(null);
    setProgreso(0);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const reset = () => {
    setEstado('idle');
    setPreviewLocal(null);
    setProgreso(0);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div style={{ fontFamily: PP, width: '100%' }}>
      {label && (
        <div style={{ fontSize: 12, fontWeight: 600, color: C.gray700, marginBottom: 6 }}>
          {label}
        </div>
      )}

      {/* Modo: imagen ya cargada (value o exito reciente) y no estamos subiendo/erroreando */}
      {(value || estado === 'exito') && estado !== 'subiendo' && estado !== 'error' && (
        <div style={{
          position: 'relative',
          border: `1px solid ${C.gray100}`,
          borderRadius: 10,
          padding: 8,
          background: C.blanco,
          ...aspectStyle,
        }}>
          <img
            src={value || previewLocal}
            alt="preview"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: 6,
              display: 'block',
            }}
          />
          {!disabled && (
            <div style={{
              position: 'absolute',
              bottom: 8, right: 8,
              display: 'flex', gap: 6,
            }}>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                style={{
                  background: C.blanco, color: C.blue700,
                  border: `1px solid ${C.blue300}`,
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: PP,
                }}
              >Cambiar</button>
              {onDelete && (
                <button
                  type="button"
                  onClick={eliminar}
                  style={{
                    background: C.blanco, color: C.rojo,
                    border: `1px solid ${C.rojo}`,
                    padding: '6px 12px',
                    borderRadius: 6,
                    fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', fontFamily: PP,
                  }}
                >Eliminar</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modo: subiendo */}
      {estado === 'subiendo' && (
        <div style={{
          border: `2px dashed ${C.blue500}`,
          borderRadius: 10,
          padding: 20,
          background: C.blue100,
          textAlign: 'center',
          ...aspectStyle,
          display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', gap: 12,
        }}>
          {previewLocal && (
            <img src={previewLocal} alt="" style={{ maxWidth: 80, maxHeight: 80, opacity: 0.5, borderRadius: 6 }} />
          )}
          <div style={{ fontSize: 13, fontWeight: 600, color: C.blue700 }}>
            Subiendo... {progreso}%
          </div>
          <div style={{
            width: '80%', height: 6, background: C.gray100, borderRadius: 3, overflow: 'hidden',
          }}>
            <div style={{
              width: `${progreso}%`, height: '100%', background: C.blue500,
              transition: 'width 0.2s',
            }} />
          </div>
        </div>
      )}

      {/* Modo: idle (sin value) o error */}
      {!value && estado !== 'subiendo' && estado !== 'exito' && (
        <div
          onClick={() => !disabled && inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); if (!disabled) setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          style={{
            border: `2px dashed ${dragActive ? C.blue500 : (estado === 'error' ? C.rojo : C.gray100)}`,
            borderRadius: 10,
            padding: 20,
            background: dragActive ? C.blue100 : C.gray50,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            transition: 'all 0.2s',
            ...aspectStyle,
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center', alignItems: 'center', gap: 8,
          }}
        >
          <div style={{ fontSize: 28 }}>📷</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.gray700 }}>
            {dragActive ? 'Suelta la imagen aquí' : 'Clic o arrastra una imagen'}
          </div>
          <div style={{ fontSize: 11, color: C.gray500 }}>
            {acceptedTypes.join(', ').toUpperCase()} · max {maxSizeMB} MB
          </div>
          {estado === 'error' && error && (
            <div style={{
              marginTop: 8,
              padding: '6px 12px',
              background: '#FEE2E2',
              color: C.rojo,
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              maxWidth: '90%',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span>{error}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); reset(); }}
                style={{
                  background: 'transparent', border: 'none', color: C.rojo,
                  fontWeight: 700, cursor: 'pointer', fontFamily: PP,
                  fontSize: 14, lineHeight: 1, padding: 0,
                }}
                aria-label="Cerrar error"
              >×</button>
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptStr}
        {...(capture ? { capture } : {})}
        onChange={onFileChange}
        disabled={disabled}
        style={{ display: 'none' }}
      />
    </div>
  );
}
